import * as React from "react";
import { Box, Typography } from "@mui/material";
import { parseKSTDate } from "../../../utils/dateUtils";

type Props = {
  nextDropAt: string;
  profileCompletionPercent?: number;
};

function formatTimeRemaining(nextDropAt: string): string {
  const now = new Date();
  const drop = parseKSTDate(nextDropAt);
  const diffMs = drop.getTime() - now.getTime();
  if (diffMs <= 0) return "새 카드 도착!";
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  if (days > 0) return `${days}일 ${hours}시간`;
  if (hours > 0) return `${hours}시간 ${minutes}분`;
  return `${minutes}분`;
}

export default function NextDropCard({ nextDropAt, profileCompletionPercent = 0 }: Props) {
  const [timeRemaining, setTimeRemaining] = React.useState(() =>
    formatTimeRemaining(nextDropAt)
  );

  React.useEffect(() => {
    const interval = setInterval(() => {
      setTimeRemaining(formatTimeRemaining(nextDropAt));
    }, 60000);
    return () => clearInterval(interval);
  }, [nextDropAt]);

  return (
    <Box
      sx={{
        bgcolor: "white",
        borderRadius: "18px",
        border: "1px solid #E2E8F0",
        p: "22px",
      }}
    >
      <Typography
        sx={{
          fontFamily: '"Playfair Display", serif',
          fontSize: "16px",
          fontWeight: 700,
          color: "#0D1F3C",
          mb: "16px",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        👤 내 프로필
      </Typography>

      {/* 완성도 % */}
      <Box sx={{ mb: "14px" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            mb: "8px",
          }}
        >
          <Typography sx={{ fontSize: "13px", color: "#4A5A72" }}>완성도</Typography>
          <Typography
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontSize: "22px",
              fontWeight: 700,
              color: "#0D1F3C",
            }}
          >
            {profileCompletionPercent}%
          </Typography>
        </Box>
        <Box
          sx={{
            height: "6px",
            bgcolor: "#F0F3F8",
            borderRadius: "3px",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              height: "100%",
              width: `${profileCompletionPercent}%`,
              background: "linear-gradient(90deg, #1A3360, #C9A86C)",
              borderRadius: "3px",
            }}
          />
        </Box>
        <Typography
          sx={{ fontSize: "12px", color: "#8896AB", mt: "10px", lineHeight: 1.5 }}
        >
          사진과 관심사를 추가하면
          <br />
          매칭 확률이 높아집니다.
        </Typography>
      </Box>

      {/* 완성하기 버튼 */}
      <Box
        component="button"
        sx={{
          width: "100%",
          height: "44px",
          bgcolor: "transparent",
          border: "1.5px solid #D4BFA0",
          borderRadius: "10px",
          color: "#8A6A40",
          fontSize: "13px",
          fontWeight: 600,
          fontFamily: "Pretendard, sans-serif",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "6px",
          transition: "all 0.15s",
          mt: "14px",
          "&:hover": { borderColor: "#C9A86C", bgcolor: "#FAF7F0" },
        }}
      >
        ✏️ 프로필 완성하기
      </Box>

      {/* 다음 카드 도착 (보조) */}
      <Box
        sx={{
          mt: "16px",
          pt: "14px",
          borderTop: "1px solid #E2E8F0",
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <Typography sx={{ fontSize: "12px", color: "#8896AB" }}>
          ⏰ 다음 카드 도착까지
        </Typography>
        <Typography
          sx={{
            fontFamily: '"Playfair Display", serif',
            fontSize: "14px",
            fontWeight: 700,
            color: "#0D1F3C",
            ml: "auto",
          }}
        >
          {timeRemaining}
        </Typography>
      </Box>
    </Box>
  );
}
